import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Payload } from '@gsm/entities';
import { isPayload } from 'app/shared/gsm/gsm-ui-lib-elements/src/utils';

import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { GsmErrorService } from './gsm-error.service';
import { GSM_URL_PREFIX } from './gsm-url-prefix';

@Injectable()
export class GsmErrorInterceptor implements HttpInterceptor {
  protected readonly gsmUrlPrefix = inject(GSM_URL_PREFIX);
  protected errorService = inject(GsmErrorService);


  public intercept<T, U>(
    req: HttpRequest<U>,
    next: HttpHandler
  ): Observable<HttpEvent<Payload<T>>> {
    if (!req.url.startsWith(this.gsmUrlPrefix)) {
      return next.handle(req);
    }

    return next.handle(req)
      .pipe(
        // handle business errors, throw error if success response has errors.
        tap({
          // success, 200 <= HTTP-Status < 300
          next: event => {
            if (event instanceof HttpResponse && event.body) {
              // take care of business errors for success.
              if (this.errorService.hasPayloadBusinessErrors(event.body)) {
                this.errorService.handleBusinessErrors(event.body);
                // TODO: throw something like GsmBusinessError.
              }

              // if technical errors are on payload, throw error.
              if (event.body.errors) {
                throw new HttpErrorResponse({
                  error:      event.body,
                  status:     event.status,
                  statusText: event.statusText,
                  url:        event.url,
                  headers:    event.headers
                });
              }
            }
          },
          // take care of business errors in case of http error status.
          error: err => {
            if (
              err instanceof HttpErrorResponse &&
              err.error &&
              this.errorService.hasPayloadBusinessErrors(err.error)
            ) {
              this.errorService.handleBusinessErrors(err.error);
            }
          }
        }),
        map(response => {
          // we assume that json responses are of type Payload<?> or of Array<?>
          // Arrays will be passed through, otherwise `payload.data` or null is returned.
          if (
            response instanceof HttpResponse &&
            response.headers.has('content-type') &&
            response.headers.get('content-type')
              .includes('application/json')
          ) {
            const body: any | null = response.body;

            let data;
            if (Array.isArray(body)) {
              data = body;
            } else if (isPayload(body)) {
              data = (body && body.data) || null;
            } else if (body.gsm) {
              // this is probably a file metadata response, which, unfortunately is not wrapped in a Payload object
              data = body;
            } else {
              // ! Webstorm incorrectly marks this as "can be simplified...". `body` might indeed be null, though!
              // unlikely, the "} else if (body.gsm) {" above would cause an error.
              if (body !== null) {
                console.warn(
                  `Response is not of type payload. The actual response will be discarded: '${JSON.stringify(
                    body
                  )}'`
                );
              }
              // changing this would require thorough re-testing everything
              data = null;
            }
            return response.clone({
              body: data
            });
          }

          return response;
        })
      );
  }
}
